import { useMemo, useState, useEffect } from "react";
import {
  addDays,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import type { AgendaSettings, AppointmentStatus } from "@bookdental/schemas";
import { MobileDayView } from "./MobileDayView";

type Appt = {
  id: string;
  patientName: string;
  startTime: string;
  endTime: string;
  status: AppointmentStatus;
  color?: string | null;
  date: Date | string;
};

type Props = {
  anchorDate: Date;
  appointments: Appt[];
  settings: AgendaSettings;
  onAppointmentClick?: (id: string) => void;
};

const WEEK_DAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"];

export function MobileMonthView({
  anchorDate,
  appointments,
  settings,
  onAppointmentClick,
}: Props) {
  const monthStart = startOfMonth(anchorDate);
  const monthEnd = endOfMonth(anchorDate);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const days = useMemo(() => {
    const arr: Date[] = [];
    let cur = gridStart;
    while (cur <= gridEnd) {
      arr.push(new Date(cur));
      cur = addDays(cur, 1);
    }
    return arr;
  }, [gridStart, gridEnd]);

  const countsByDay = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of appointments) {
      const k = new Date(a.date).toDateString();
      map.set(k, (map.get(k) ?? 0) + 1);
    }
    return map;
  }, [appointments]);

  const [selected, setSelected] = useState<Date>(anchorDate);
  useEffect(() => {
    if (!isSameMonth(selected, anchorDate)) setSelected(anchorDate);
  }, [anchorDate, selected]);

  const today = new Date();

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-7 bg-zinc-100 text-center text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
        {WEEK_DAYS.map((d) => (
          <div key={d} className="py-2">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 bg-white">
        {days.map((d) => {
          const inMonth = isSameMonth(d, anchorDate);
          const isSelected = isSameDay(d, selected);
          const isToday = isSameDay(d, today);
          const count = countsByDay.get(d.toDateString()) ?? 0;
          return (
            <button
              key={d.toISOString()}
              type="button"
              onClick={() => setSelected(d)}
              className={`relative flex aspect-square flex-col items-start justify-start border-b border-r border-zinc-100 p-1.5 text-left transition-colors ${
                isSelected
                  ? "bg-brand-600 text-white"
                  : inMonth
                    ? "bg-white text-zinc-900 active:bg-zinc-50"
                    : "bg-zinc-50/60 text-zinc-400"
              }`}
            >
              <span
                className={`text-[13px] font-semibold leading-none tabular-nums ${
                  isToday && !isSelected ? "text-brand-700" : ""
                }`}
              >
                {format(d, "d")}
              </span>
              {count > 0 && (
                <span
                  className={`absolute bottom-2 left-1/2 h-1.5 w-6 -translate-x-1/2 rounded-full ${
                    isSelected ? "bg-white/80" : "bg-brand-300"
                  }`}
                />
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-2 border-t border-zinc-200 bg-brand-600 px-4 py-2 text-[13px] font-semibold uppercase tracking-wide text-white">
        Horários
      </div>
      <MobileDayView
        date={selected}
        appointments={appointments}
        settings={settings}
        onAppointmentClick={onAppointmentClick}
      />
    </div>
  );
}
